"use client";

import { Flame } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import type { ProblemDifficulty } from "../types/difficulty";

const ROSE = "bg-rose-600 dark:bg-rose-400";

/** 등급별 화면 표기·채워지는 막대 수(4칸 중)·막대 색. 내부 등급 '극하'는 학습자에게 '최하'로 보인다 (JCG-3917). */
const LEVELS: Record<ProblemDifficulty, { label: string; filled: number; color: string }> = {
  극하: { label: "최하", filled: 1, color: "bg-cyan-600 dark:bg-cyan-400" },
  하: { label: "하", filled: 2, color: "bg-emerald-600 dark:bg-emerald-400" },
  중: { label: "중", filled: 3, color: "bg-amber-600 dark:bg-amber-400" },
  상: { label: "상", filled: 4, color: ROSE },
  극상: { label: "극상", filled: 4, color: ROSE },
};

const BAR_HEIGHTS = ["h-1", "h-[7px]", "h-2.5", "h-[13px]"] as const;

/** 문제 카드와 난이도 선택에서 공유하는 비대화형 그래프·이름. */
export function DifficultyIndicator({ difficulty }: { difficulty: ProblemDifficulty }) {
  const { label, filled, color } = LEVELS[difficulty];
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex h-[13px] items-end gap-0.5" aria-hidden="true">
        {BAR_HEIGHTS.map((height, index) => (
          <span
            key={height}
            className={`w-[3px] rounded-[1px] ${height} ${index < filled ? color : "bg-gray-300 dark:bg-gray-600"}`}
          />
        ))}
      </span>
      {label}
      {difficulty === "극상" && (
        <Flame className="-ml-0.5 h-3 w-3 text-rose-600 dark:text-rose-400" aria-hidden="true" />
      )}
    </span>
  );
}

/**
 * 문제 카드 헤더의 난이도 칩. 막대 4칸으로 단계를 보여 주고, 탭·호버하면 기준을 설명한다.
 * 색은 막대에만 써서 유형 뱃지(빨강·파랑)·업글 버튼(주황)과 겹치지 않게 한다.
 */
export default function DifficultyBadge({ difficulty }: { difficulty: ProblemDifficulty }) {
  const { label } = LEVELS[difficulty];
  return (
    <Tooltip tapToOpen>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`난이도 ${label}`}
          className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-gray-300 bg-white px-2 text-xs font-semibold text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <DifficultyIndicator difficulty={difficulty} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-72 text-left text-sm">
        <b>난이도 {label}</b> · 감자가 실기 기출과 비교해 매긴 난이도예요. 최하·하·중·상·극상 5단계이고, 진도가 급하면 상 이상은 나중에 풀어도 괜찮아요.
      </TooltipContent>
    </Tooltip>
  );
}
